<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.ProgressClaimsIndexLite.CreateClaimModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex h-full w-full overflow-y-auto">
        <div class="flex flex-col w-full my-2 px-3">
          <form
            action=""
            class="w-full flex flex-col space-y-4"
            @submit.prevent="createClaim"
          >
            <i18n-t
              v-if="claimForm.form.start && claimForm.form.end"
              keypath="HeadContract.ProgressClaimsIndexLite.CreateClaimModal.overridingClaimPeriod"
              tag="p"
            >
              <template #startDate>
                <b>{{ DateTime.fromFormat(claimForm.form.start, 'yyyy-MM-dd').toLocaleString() }}</b>
              </template>

              <template #endDate>
                <b>{{ DateTime.fromFormat(claimForm.form.end, 'yyyy-MM-dd').toLocaleString() }}</b>
              </template>
            </i18n-t>

            <i18n-t
              v-else-if="latestClaim"
              keypath="HeadContract.ProgressClaimsIndexLite.CreateClaimModal.nextClaimPeriod"
              tag="p"
            >
              <template #startDate>
                <b>{{ latestClaim.startDate?.plus({ months: 1 }).startOf('month').toLocaleString() }}</b>
              </template>

              <template #endDate>
                <b>{{ latestClaim.endDate?.plus({ months: 1 }).endOf('month').toLocaleString() }}</b>
              </template>
            </i18n-t>

            <i18n-t
              v-else
              keypath="HeadContract.ProgressClaimsIndexLite.CreateClaimModal.firstClaimPeriod"
              tag="p"
            >
              <template #startDate>
                <b>{{ DateTime.fromISO(project.claim_start).startOf('month').toLocaleString() }}</b>
              </template>

              <template #endDate>
                <b>{{ DateTime.fromISO(project.claim_start).startOf('month').endOf('month').toLocaleString() }}</b>
              </template>
            </i18n-t>

            <label>
              {{ $t('HeadContract.ProgressClaimsIndexLite.CreateClaimModal.label.startOverride') }}
              <AppInput
                v-model="claimForm.form.start"
                name="start"
                type="date"
                class="form-input"
                :error="claimForm.getErrors('start')"
              />
            </label>

            <label>
              {{ $t('HeadContract.ProgressClaimsIndexLite.CreateClaimModal.label.endOverride') }}
              <AppInput
                v-model="claimForm.form.end"
                name="end"
                type="date"
                class="form-input"
                :error="claimForm.getErrors('end')"
              />
            </label>
          </form>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <div>
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click.prevent="$emit('close')"
          >
            {{ $t('HeadContract.ProgressClaimsIndexLite.CreateClaimModal.button.cancel') }}
          </button>

          <button
            type="button"
            class="btn btn--primary-blue ml-2"
            @click="createClaim"
          >
            {{ $t('HeadContract.ProgressClaimsIndexLite.CreateClaimModal.button.create') }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import Claim from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const props = withDefaults(
  defineProps<{
    projectId: Id;
    latestClaim?: Claim;
    boqType: string;
  }>(),
  {
    latestClaim: undefined,
  },
);

const emits = defineEmits<{
  (e: 'close');
}>();

const router = useRouter();
const claimsStore = useClaimsStore();

const claimForm = useFormData({
  start: '',
  end: '',
  boq_type: props.boqType,
});

const createClaimAction = useStoreApiAction(claimsStore.createClaim);

const createClaim = () => {
  claimForm.resetErrors();

  createClaimAction.request(props.projectId, claimForm.form).then((claim) => {
    Swal.fire({
      icon: 'success',
      title: 'Claim Created',
    }).then(() => {
      props.boqType === 'direct' ?
        router.push({
          name: 'ProgressClaims',
          params: {
            claimId: claim.id,
          },
        }) :
        router.push({
          name: 'IndirectProgressClaimsShow',
          params: {
            claimId: claim.id,
          },
        });

      emits('close');
    });
  }).catch((error) => {
    claimForm.setErrors(error.data);
  });
};

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});
</script>

<style scoped></style>
